import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';

import UsersService from '../../../data/users-service';
import Auth from '../../../data/auth-service';
import DataContext from '../../../data/data-context';

const useStyles = makeStyles(theme => ({
    formWidth: {
        minWidth: '400px',
    },
    select: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        width: '100%',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        minHeight: 36,
    },
    chip: {
        margin: 2,
        background: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        fontSize: 16,
        '& .MuiChip-deleteIcon': {
            color: 'rgba(255, 255, 255, 0.75)',
        },
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
}));

const Health = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { systemSettings, updateSystemSettings } = useContext(DataContext);
    const [health, setHealth] = useState({
        ha_all_to_email: null, // always null
        ha_emails: [],
        ha_users: [], // usernames
    });
    const [original, setOriginal] = useState();
    const [users, setUsers] = useState([]);
    const [changes, setChanges] = useState(false);
    const healthPerms = Auth.permissions.get('HealthAlerts');

    useEffect(() => {
        UsersService.getUsers().then(res => {
            setUsers(res.data);
        });
    }, []);

    useEffect(() => {
        setChanges(!_isEqual(health, original));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [health]);

    useEffect(() => {
        if (systemSettings && systemSettings.sensorHealthAlerts) {
            const h = _cloneDeep(systemSettings.sensorHealthAlerts);
            if (!h.ha_emails) {
                h.ha_emails = [];
            }
            if (!h.ha_users) {
                h.ha_users = [];
            }
            setHealth(_cloneDeep(h));
            setOriginal(_cloneDeep(h));
        }
    }, [systemSettings]);

    const handleUserRecipients = (e) => {
        setHealth({
            ...health,
            ha_users: e.target.value,
        });
    };

    const handleEmailRecipients = (e, validID, valid) => {
        setHealth({
            ...health,
            ha_emails: e.target.value ? e.target.value.replace(/\s/g, '').split(',') : [],
        });
    };

    const handleRemoveUser = (username) => {
        const h = { ...health };
        h.ha_users.splice(h.ha_users.findIndex(v => v === username), 1);
        setHealth(h);
    };

    const handleSave = () => {
        updateSystemSettings({ sensorHealthAlerts: health });
    };

    const handleCancel = () => {
        navigate('/system/configuration');
    };

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    return (
        <Grid container>
            <Grid item xs={8}>
                <Paper elevation={3}>
                    <Box p={5}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl className={classes.select}>
                                    <InputLabel>User Recipients</InputLabel>
                                    <Select
                                        disabled={!healthPerms.write}
                                        labelId="user-select-label"
                                        aria-label="user select label"
                                        value={health.ha_users}
                                        multiple
                                        onChange={handleUserRecipients}
                                        input={<Input />}
                                        renderValue={(usersSelected) => (
                                            <div className={classes.chips}>
                                                {usersSelected.map((v, i) => {
                                                    return (
                                                        <Chip
                                                            disabled={!healthPerms.delete}
                                                            key={'user-chip-' + i}
                                                            label={v}
                                                            className={classes.chip}
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation();
                                                            }}
                                                            onDelete={() => {
                                                                handleRemoveUser(v);
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        )}
                                        MenuProps={MenuProps}
                                    >
                                        {users.map((v, i) => {return <MenuItem key={'user-select-' + i} value={v.username}>{v.username} - {v.name}</MenuItem>})}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="h6">Email Recipients</Typography>
                                <TextField
                                    disabled={!healthPerms.write}
                                    label="Enter email addresses to receive alerts"
                                    aria-label="alert email addresses"
                                    helperText="Comma separated emails"
                                    margin="normal"
                                    fullWidth
                                    value={health.ha_emails}
                                    onChange={handleEmailRecipients}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                {healthPerms.write && <Button
                                    className={classes.buttonRight}
                                    variant="contained"
                                    color="primary"
                                    aria-label="save"
                                    onClick={handleSave}
                                    disabled={!changes}
                                >
                                    Save
                                </Button>}
                                {healthPerms.write && <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={handleCancel}>Cancel</Button>}
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default Health;
